import React, { useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { useCreateFleet } from '../hooks/useFleets'
import { FleetConflictError } from '../service/fleetAPI'
import { useAuth } from '../../auth/hooks'
import { FleetUiStrings } from '../i18n/fleetI18n'
import { useToast } from '../../../common/providers/ToastProvider/useToast'
import { Fleet } from '../types/fleet.types'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'

interface CreateFleetModalProps {
  open: boolean
  onClose: () => void
  onCreated?: (fleet: Fleet) => void
  strings: FleetUiStrings
}

const CreateFleetModal: React.FC<CreateFleetModalProps> = ({
  open,
  onClose,
  onCreated,
  strings,
}) => {
  const [name, setName] = useState('')
  const createMutation = useCreateFleet()
  const { getAccountIdUser } = useAuth()
  const toast = useToast()

  const handleCreate = () => {
    const accountId = getAccountIdUser()
    if (!name.trim() || !accountId) return
    createMutation.mutate(
      {
        payload: {
          fleet_name: name.trim(),
          fleet_account_id: accountId,
          member_dev_euis: [],
        },
      },
      {
        onSuccess: (res) => {
          toast.success(strings.createFleet)
          setName('')
          onClose()
          if (res) onCreated?.(res as Fleet)
        },
        onError: (err) => {
          if (err instanceof FleetConflictError) {
            toast.error(strings.conflictError)
            return
          }
          toast.error(strings.genericError)
        },
      },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{strings.createFleet}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={strings.fleetName}
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate()
          }}
        />
      </DialogContent>
      <DialogActions>
        <ButtonV2
          onClick={onClose}
          variant="outlined"
          color="secondary"
          size="medium"
        >
          {strings.cancel}
        </ButtonV2>
        <ButtonV2
          onClick={handleCreate}
          variant="contained"
          color="primary"
          size="medium"
          disabled={createMutation.isLoading || !name.trim()}
        >
          {createMutation.isLoading ? strings.saving : strings.save}
        </ButtonV2>
      </DialogActions>
    </Dialog>
  )
}

export default CreateFleetModal
